import { Component, inject, computed, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OfflineService } from '../../../../services/pwa/offline.service';
import { PWAService } from '../../../../services/pwa/pwa.service';

@Component({
    selector: 'lc-offline-indicator',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './offline-indicator.component.html',
    styleUrls: ['./offline-indicator.component.scss']
})
export class OfflineIndicatorComponent {
    private readonly offlineService = inject(OfflineService);
    private readonly pwaService = inject(PWAService);

    // Signals computados
    readonly isOffline = this.offlineService.isOffline;
    readonly pendingActionsCount = this.offlineService.pendingActionsCount;
    readonly syncProgress = this.offlineService.syncProgress;
    readonly lastSync = this.offlineService.lastSync;

    // Estado local para controle de sincronização
    private isSyncingState = false;

    // Estado de reconexão
    readonly isReconnected = signal<boolean>(false);
    private wasOfflineBefore = false;
    private reconnectTimeout: any = null;

    constructor() {
        // Detectar quando volta de offline para online
        effect(() => {
            const currentlyOffline = this.isOffline();

            // Se estava offline e agora está online, mostrar indicador de reconexão
            if (this.wasOfflineBefore && !currentlyOffline) {
                this.showReconnected();
            }

            // Atualizar estado anterior
            this.wasOfflineBefore = currentlyOffline;
        }, { allowSignalWrites: true });
    }

    /**
     * Mostra indicador de reconexão temporariamente
     */
    private showReconnected(): void {
        this.isReconnected.set(true);

        // Limpar timeout anterior se existir
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        // Esconder após 3 segundos
        this.reconnectTimeout = setTimeout(() => {
            this.isReconnected.set(false);
        }, 3000);
    }

    /**
     * Verifica se deve mostrar o indicador
     */
    shouldShowIndicator(): boolean {
        // Mostrar quando reconectado
        if (this.isReconnected()) {
            return true;
        }

        // Mostrar sempre quando offline
        if (this.isOffline()) {
            return true;
        }

        // Mostrar quando sincronizando
        if (this.isSyncingState) {
            return true;
        }

        // Mostrar quando há ações pendentes
        if (this.pendingActionsCount() > 0) {
            return true;
        }

        // Mostrar brevemente após sincronização (2 segundos apenas)
        if (this.lastSync()) {
            const timeSinceSync = Date.now() - this.lastSync()!.getTime();
            return timeSinceSync < 2000; // 2 segundos
        }

        return false;
    }

    /**
     * Verifica se está sincronizando
     */
    isSyncing(): boolean {
        return this.isSyncingState;
    }

    /**
     * Verifica se deve mostrar botão de ação
     */
    showActionButton(): boolean {
        // Mostrar quando offline com ações pendentes
        if (this.isOffline() && this.pendingActionsCount() > 0) {
            return true;
        }

        // Mostrar quando há ações pendentes e está online
        if (!this.isOffline() && this.pendingActionsCount() > 0) {
            return true;
        }

        return false;
    }

    /**
     * Obtém texto do botão de ação
     */
    getActionButtonText(): string {
        if (this.isOffline()) {
            return 'Tentar novamente';
        }

        if (this.pendingActionsCount() > 0) {
            return `Sincronizar (${this.pendingActionsCount()})`;
        }

        return 'Sincronizar';
    }

    /**
     * Manipula ação do botão
     */
    async handleAction(): Promise<void> {
        if (this.isSyncing()) {
            return;
        }

        this.isSyncingState = true;

        try {
            await this.offlineService.forcarSincronizacao();
        } catch (error) {
            console.error('Erro ao sincronizar:', error);
        } finally {
            this.isSyncingState = false;
        }
    }
}
